import './SalesforceSetupPanel.scss';

import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { Dayjs } from 'dayjs';
import { SalesforceOrganizationSyncConfigurationButton } from './SalesforceOrganizationSyncConfigurationButton';
import { SalesforceOrganizationSyncControls } from './SalesforceOrganizationSyncControls';
import { SalesforceOrganizationTypeTag } from './SalesforceOrganizationTypeTag';
import { SalesforceOrgSyncSecurityAlertsControls } from './SalesforceOrgSyncSecurityAlertsControls';
import { ShardSearchEntryDetails } from '@sonar-software/sonar-admin';
import { SonarTable } from '@ui/SonarTable';

import { coreActions, OrganizationMetadata } from '@sonar-software/sonar-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useAction, useInterval, useOnMount, useSonarSelector } from '@hooks';

type SalesforceOrganizationWithShardName = OrganizationMetadata & { shardName?: string };

const getAllSalesforceOrganizationsSelectors = createAsyncSelectors(
	(state) => state.core.organizations.salesforce.all.get
);

const salesforceSetupTableColumns = [
	{
		dataIndex: 'name',
		key: 'name',
		title: ADMIN_DASHBOARD_COPY.ORGANZATION_NAME,
		width: 300
	},
	{
		dataIndex: 'isSandbox',
		key: 'sandbox',
		render: (isSandbox: boolean) => <SalesforceOrganizationTypeTag isSandbox={ isSandbox } />,
		title: ADMIN_DASHBOARD_COPY.IS_SANDBOX,
		width: 100
	},
	{
		dataIndex: 'instanceUrl',
		key: 'instanceUrl',
		render: (instanceUrl: string) => <a href={ instanceUrl }>{ instanceUrl }</a>,
		title: ADMIN_DASHBOARD_COPY.INSTANCE_URL,
		width: 300
	},
	{
		dataIndex: 'salesforceUsername',
		key: 'salesforceUsername',
		title: ADMIN_DASHBOARD_COPY.SALESFORCE_USERNAME,
		width: 300
	},
	{
		dataIndex: 'id',
		key: 'id',
		title: ADMIN_DASHBOARD_COPY.ID,
		width: 75
	},
	{
		align: 'center' as const,
		key: 'importConfig',
		render: (organization: SalesforceOrganizationWithShardName) =>
			<SalesforceOrganizationSyncConfigurationButton
				organizationId={ organization?.id }
				organizationName={ organization?.name }
				shardName={ organization.shardName } />,
		width: 100
	},
	{
		dataIndex: 'latestIndexTime',
		key: 'lastSync',
		render: (latestIndexTime: Dayjs, organization: SalesforceOrganizationWithShardName) =>
			<SalesforceOrganizationSyncControls
				organization={ organization }
				shardName={ organization.shardName } />,
		title: ADMIN_DASHBOARD_COPY.LAST_SYNC,
		width: 600
	},
	{
		key: 'syncSecurityAlerts',
		render: (organization: SalesforceOrganizationWithShardName) =>
			<SalesforceOrgSyncSecurityAlertsControls
				organization={ organization } />,
		title: ADMIN_DASHBOARD_COPY.SYNC_SECURITY_ALERTS,
		width: 100
	}
];

export interface SalesforceSetupPanelProps {
	id?: string;
	searchDetails?: ShardSearchEntryDetails;
}

export const SalesforceSetupPanel: React.FC<SalesforceSetupPanelProps> = (props) => {
	const { id, searchDetails } = props;

	const allSalesforceOrganizations = useSonarSelector(getAllSalesforceOrganizationsSelectors.data);

	const [ allSalesforceOrganizationsWithShards, setAllSalesforceOrganizationsWithShards ] =
		useState<Array<SalesforceOrganizationWithShardName>>([]);

	const getAllSalesforceOrganizations = useAction(coreActions.requestGetCoreAllSalesforceOrganizationsByCustomer);

	const refreshOrganizations = useCallback(() => {
		if (searchDetails) {
			const { customerId, shardName } = searchDetails;

			getAllSalesforceOrganizations({
				customerId,
				shardName
			});
		}
	}, [ searchDetails, getAllSalesforceOrganizations ]);

	useEffect(() => {
		if (searchDetails) {
			const orgsWithShardNames: SalesforceOrganizationWithShardName[] = [];
			if (allSalesforceOrganizations) {
				const { shardName } = searchDetails;

				allSalesforceOrganizations.organizations.forEach((element: OrganizationMetadata) => {
					orgsWithShardNames.push(
						{
							shardName,
							...element
						}
					);
				});
			}

			setAllSalesforceOrganizationsWithShards(orgsWithShardNames);
		}
	}, [ allSalesforceOrganizations, searchDetails ]);

	useOnMount(() => {
		refreshOrganizations();
	});

	useInterval(refreshOrganizations, 5000);

	return (
		<SonarTable
			columns={ salesforceSetupTableColumns }
			dataSource={ allSalesforceOrganizationsWithShards }
			id={ id }
			pagination={ false }
			rowKey='id'
			scroll={ { x: 'max-content' } } />
	);
};
